import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpDown } from "lucide-react";
import useUser from "../../../../hooks/useUser";
import Button from "../../../components/Buttons/Button/Button";
import Pagination from "../../../components/Pagination/Pagination";
import Combobox from "../../../components/Combobox/Combobox";
import { getSubscriptionPaymentHistory } from "../../../../services/subscriptionsService";
import "./PaymentHistory.css";

export default function PaymentHistory() {
  const { user } = useUser();
  const { t } = useTranslation("suscriptions");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [sortColumn, setSortColumn] = useState("created");
  const [sortDirection, setSortDirection] = useState("desc");

  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [amountMin, setAmountMin] = useState("");
  const [amountMinDraft, setAmountMinDraft] = useState("");
  const [subscriptionName, setSubscriptionName] = useState("");
  const [subscriptionOptions, setSubscriptionOptions] = useState([]);

  const PAGE_SIZE = 10;

  const organizationId = useMemo(() => {
    const org = user?.organization || user?.organization_id;
    if (!org) return null;
    if (typeof org === "object") {
      return org.id || org._id || null;
    }
    return org;
  }, [user?.organization, user?.organization_id]);

  const parseAmountNumber = useCallback((value) => {
    const raw = String(value ?? "").trim();
    if (!raw) return null;

    // Allow comma decimal ("1750,00") and ignore thousands separators.
    // If comma is used as decimal, dots are treated as thousands separators.
    const hasComma = raw.includes(",");
    const normalized = hasComma
      ? raw.replace(/\./g, "").replace(/,/g, ".")
      : raw;

    const cleaned = normalized.replace(/[^0-9.-]/g, "");
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : null;
  }, []);

  const normalizeAmount2 = useCallback((value) => {
    const n = parseAmountNumber(value);
    if (n === null) return "";
    // keep comma style in UI
    return n.toFixed(2).replace(".", ",");
  }, [parseAmountNumber]);

  const getBillingCycle = useCallback(
    (item) =>
      item?.billing_cycle ||
      item?.plan_info?.billing_cycle ||
      item?.plan_info?.stripe_id?.billing_cycle ||
      item?.plan_info?.stripe_id?.price?.billing_cycle ||
      item?.plan_info?.stripe_id?.price?.interval ||
      item?.plan_info?.stripe_id?.price?.recurring?.interval ||
      item?.plan_info?.stripe_id?.price?.recurring?.interval_count ||
      item?.plan_info?.stripe_id?.interval ||
      item?.plan_info?.price?.billing_cycle ||
      item?.plan_info?.price?.recurring?.interval ||
      item?.plan_info?.price?.interval ||
      item?.stripe_invoice?.data?.lines?.data?.[0]?.price?.recurring?.interval ||
      item?.stripe_invoice?.data?.lines?.data?.[0]?.plan?.interval ||
      "",
    []
  );

  const fetchAllHistoryPages = async (baseFilters) => {
    let currentPage = 1;
    const pageSize = 100;
    let allResults = [];
    let hasNext = true;

    while (hasNext) {
      const res = await getSubscriptionPaymentHistory({
        ...baseFilters,
        page: currentPage,
        page_size: pageSize,
      });

      const pageResults = Array.isArray(res?.results) ? res.results : [];
      allResults = allResults.concat(pageResults);

      if (!res?.next) {
        hasNext = false;
      } else {
        currentPage += 1;
      }
    }

    return {
      count: allResults.length,
      results: allResults,
    };
  };

  useEffect(() => {
    if (!organizationId) return;
    const fetchHistory = async () => {
      setLoading(true);
      setError(null);
      try {
        const filters = {
          organization_id: organizationId,
        };

        if (status) {
          filters.status = status;
        }

        if (dateFrom) {
          filters.payment_date_from = dateFrom;
        }
        if (dateTo) {
          filters.payment_date_to = dateTo;
        }

        const toCents = (value) => {
          const n = parseAmountNumber(value);
          return n === null ? null : Math.round(n * 100);
        };

        const minCents = toCents(amountMin);

        if (minCents !== null) {
          // Minimum amount filter
          filters.amount_paid_min = minCents;
        }

        const fullData = await fetchAllHistoryPages(filters);

        const names = new Set(
          (Array.isArray(fullData?.results) ? fullData.results : [])
            .map((r) => String(r?.subscription_name || "").trim())
            .filter(Boolean)
        );
        const options = Array.from(names)
          .sort((a, b) => a.localeCompare(b))
          .map((name) => ({ value: name, label: name }));
        setSubscriptionOptions([
          { value: "", label: t("page.paymentHistory.filters.allSubscriptions") },
          ...options,
        ]);

        const applyLocalAmountFilter = (rows) => {
          if (!amountMin) return rows;
          const minAmountCents = toCents(amountMin);
          if (minAmountCents === null) return rows;
          return (Array.isArray(rows) ? rows : []).filter(
            (r) => typeof r?.amount_paid === "number" && r.amount_paid >= minAmountCents
          );
        };

        if (subscriptionName) {
          const filteredData = await fetchAllHistoryPages({
            ...filters,
            subscription_name: subscriptionName,
          });

          const backendResults = Array.isArray(filteredData?.results)
            ? filteredData.results
            : [];

          if (backendResults.length > 0) {
            // Ensure Amount filter works even if backend ignores amount_paid_max
            const localAmountFiltered = applyLocalAmountFilter(backendResults);
            setData({ count: localAmountFiltered.length, results: localAmountFiltered });
          } else {
            const localResults = (Array.isArray(fullData?.results) ? fullData.results : [])
              .filter((r) => String(r?.subscription_name || "").trim() === subscriptionName);
            const localAmountFiltered = applyLocalAmountFilter(localResults);
            setData({ count: localAmountFiltered.length, results: localAmountFiltered });
          }
        } else {
          const localAmountFiltered = applyLocalAmountFilter(fullData?.results);
          setData({ count: localAmountFiltered.length, results: localAmountFiltered });
        }
      } catch (e) {
        console.error("Error loading payment history", e?.response || e);
        setError(t("page.errorPaymentHistory"));
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [organizationId, t, status, dateFrom, dateTo, amountMin, subscriptionName, parseAmountNumber]);

  const results = useMemo(
    () => (Array.isArray(data?.results) ? data.results : []),
    [data]
  );

  const getSortValue = useCallback((item, column) => {
    const getDateValue = (value) => {
      if (!value) return 0;
      const ts = new Date(value).getTime();
      return Number.isFinite(ts) ? ts : 0;
    };

    const paymentDate = item.payment_date || item.paid_at || item.paid_date;
    const createdDate = item.created_at || item.created || item.date_created;

    const username =
      item?.stripe_customer?.data?.user?.username ||
      item?.stripe_customer?.data?.user?.name ||
      item?.user?.username ||
      item?.user?.name ||
      "";

    const rawAmount = (() => {
      if (typeof item.amount_paid === "number") return item.amount_paid / 100;
      const display = item.amount_display;
      if (typeof display === "string") {
        const parsed = parseFloat(display.replace(/[^0-9.-]+/g, ""));
        return Number.isFinite(parsed) ? parsed : 0;
      }
      return 0;
    })();

    const billingCycle = getBillingCycle(item);

    const paymentMethod = (item.payment_method_display || "").toLowerCase();

    switch (column) {
      case "created":
        return getDateValue(createdDate);
      case "user":
        return username.toLowerCase();
      case "subscription":
        return (item.subscription_name || "").toLowerCase();
      case "billing_cycle":
        return String(billingCycle || "").toLowerCase();
      case "amount":
        return rawAmount;
      case "payment_method":
        return paymentMethod;
      case "status":
        return (item.status || "").toLowerCase();
      case "paid":
        return getDateValue(paymentDate);
      default:
        return 0;
    }
  }, [getBillingCycle]);

  const sortedResults = useMemo(() => {
    const arr = [...results];
    const factor = sortDirection === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      const va = getSortValue(a, sortColumn);
      const vb = getSortValue(b, sortColumn);
      if (va === vb) return 0;
      if (va === undefined || va === null) return 1;
      if (vb === undefined || vb === null) return -1;
      if (va < vb) return -1 * factor;
      return 1 * factor;
    });
    return arr;
  }, [results, sortColumn, sortDirection, getSortValue]);

  const pagedResults = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return sortedResults.slice(start, start + PAGE_SIZE);
  }, [sortedResults, page, PAGE_SIZE]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const renderSortableHeader = (column, label) => (
    <th
      className="payment-history-sortable-th"
      onClick={() => handleSort(column)}
    >
      <div className="payment-history-sortable-header">
        <span>{label}</span>
        <ArrowUpDown
          size={14}
          className={`payment-history-sort-icon ${
            sortColumn === column ? "active" : ""
          } ${sortColumn === column && sortDirection === "desc" ? "desc" : ""}`}
        />
      </div>
    </th>
  );

  const totalPages = useMemo(() => {
    const count = sortedResults.length;
    return count > 0 ? Math.ceil(count / PAGE_SIZE) : 1;
  }, [sortedResults.length, PAGE_SIZE]);

  return (
    <div className="suscriptions-page space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="suscriptions-title text-2xl font-semibold mb-1">
            {t("page.paymentHistory.title")}
          </h1>
          <p className="user-mgmt-subtitle">
            {t("page.paymentHistory.subtitle")}
          </p>
        </div>
        <Button
          type="button"
          variant="primary"
          size="md"
          onClick={() => navigate("/app/suscriptions")}
        >
          {t("page.paymentHistory.backToSubscription")}
        </Button>
      </div>

      {loading && <p>{t("page.paymentHistory.loading")}</p>}
      {error && <p className="text-red-600 text-sm">{error}</p>}

      {!loading && !error && (
        <section className="suscriptions-section space-y-2">

          <div className="payment-history-filters space-y-2">
            <div className="payment-history-filters-wrapper">
              <div className="payment-history-filters-left">
                <Combobox
                  id="payment-history-subscription"
                  value={subscriptionName}
                  label={t("page.paymentHistory.filters.allSubscriptions")}
                  options={subscriptionOptions}
                  onChange={(newValue) => {
                    setPage(1);
                    setSubscriptionName(newValue);
                  }}
                  className="payment-history-filter-input"
                />

                <input
                  type="text"
                  inputMode="decimal"
                  className="payment-history-filter-input"
                  value={amountMinDraft}
                  placeholder={t("page.paymentHistory.filters.amountMin")}
                  onChange={(e) => {
                    setAmountMinDraft(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter") return;
                    const next = normalizeAmount2(amountMinDraft);
                    setAmountMinDraft(next);
                    setPage(1);
                    setAmountMin(next);
                  }}
                  onBlur={() => {
                    const next = normalizeAmount2(amountMinDraft);
                    setAmountMinDraft(next);
                    if (next !== amountMin) {
                      setPage(1);
                      setAmountMin(next);
                    }
                  }}
                />

                <Combobox
                  id="payment-history-status"
                  value={status}
                  label={t("page.paymentHistory.filters.allStatus")}
                  options={[
                    {
                      value: "",
                      label: t("page.paymentHistory.filters.allStatus"),
                    },
                    { value: "paid", label: t("page.paymentHistory.status.paid") },
                    { value: "failed", label: t("page.paymentHistory.status.failed") },
                    { value: "pending", label: t("page.paymentHistory.status.pending") },
                  ]}
                  onChange={(newValue) => {
                    setPage(1);
                    setStatus(newValue);
                  }}
                  className="payment-history-filter-input"
                />
              </div>

              <div className="payment-history-filters-dates">
                <div className="payment-history-date-row">
                  <span className="payment-history-date-label">
                    {t("page.paymentHistory.filters.dateFrom")}
                  </span>
                  <input
                    type="date"
                    className="payment-history-filter-input payment-history-date-input"
                    value={dateFrom}
                    onChange={(e) => {
                      setPage(1);
                      setDateFrom(e.target.value);
                    }}
                  />
                </div>

                <div className="payment-history-date-row">
                  <span className="payment-history-date-label">
                    {t("page.paymentHistory.filters.dateTo")}
                  </span>
                  <input
                    type="date"
                    className="payment-history-filter-input payment-history-date-input"
                    value={dateTo}
                    onChange={(e) => {
                      setPage(1);
                      setDateTo(e.target.value);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {sortedResults.length > 0 ? (
            <div className="payment-history-table-wrapper">
              <table className="payment-history-table">
                <thead>
                  <tr>
                    {renderSortableHeader(
                      "created",
                      t("page.paymentHistory.table.created")
                    )}
                    {renderSortableHeader(
                      "user",
                      t("page.paymentHistory.table.user")
                    )}
                    {renderSortableHeader(
                      "subscription",
                      t("page.paymentHistory.table.subscription")
                    )}
                    {renderSortableHeader(
                      "billing_cycle",
                      t("page.paymentHistory.table.billingCycle")
                    )}
                    {renderSortableHeader(
                      "amount",
                      t("page.paymentHistory.table.amount")
                    )}
                    {renderSortableHeader(
                      "payment_method",
                      t("page.paymentHistory.table.paymentMethod")
                    )}
                    {renderSortableHeader(
                      "status",
                      t("page.paymentHistory.table.status")
                    )}
                    {renderSortableHeader(
                      "paid",
                      t("page.paymentHistory.table.paid")
                    )}
                    <th>{t("page.paymentHistory.table.invoice")}</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedResults.map((item) => {
                    const formatDate = (value) =>
                      value ? new Date(value).toLocaleDateString() : "-";

                    const paymentDate = item.payment_date || item.paid_at || item.paid_date;
                    const createdDate = item.created_at || item.created || item.date_created;
                    const billingCycle = getBillingCycle(item);

                    const createdLabel = formatDate(createdDate);
                    const paidLabel = formatDate(paymentDate);

                    const amountLabel =
                      item.amount_display ||
                      (typeof item.amount_paid === "number"
                        ? `${(item.amount_paid / 100).toFixed(2)} USD`
                        : "-");

                    const paymentMethodLabel =
                      item.payment_method_display || "-";

                    const statusLabel = item.status || "-";

                    const invoiceUrl =
                      item.link_payment ||
                      item.invoice_url ||
                      item.hosted_invoice_url ||
                      item.stripe_invoice_url ||
                      null;

                    const username =
                      item?.stripe_customer?.data?.user?.username ||
                      item?.stripe_customer?.data?.user?.name ||
                      item?.user?.username ||
                      item?.user?.name ||
                      "-";

                    return (
                      <tr key={item.id || `${paymentDate}-${amountLabel}`}>
                        <td>{createdLabel}</td>
                        <td>{username}</td>
                        <td>{item.subscription_name || "-"}</td>
                        <td>{billingCycle || "-"}</td>
                        <td>{amountLabel}</td>
                        <td>{paymentMethodLabel}</td>
                        <td>{statusLabel}</td>
                        <td>{paidLabel}</td>
                        <td>
                          {invoiceUrl ? (
                            <a
                              href={invoiceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="payment-history-link"
                              title={t("page.paymentHistory.table.downloadDetails")}
                            >
                              <svg
                                aria-hidden="true"
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                xmlns="http://www.w3.org/2000/svg"
                                className="⚙   as-y as-z as-10 as-11 as-4k as-7a as-7b as-8c as-3w as-3y ⚙1rbqim6">
                                  <path d="M0 2.25A2.25 2.25 0 0 1 2.25 0h7.5A2.25 2.25 0 0 1 12 2.25v6a.75.75 0 0 1-1.5 0v-6a.75.75 0 0 0-.75-.75h-7.5a.75.75 0 0 0-.75.75v10.851a.192.192 0 0 0 .277.172l.888-.444a.75.75 0 1 1 .67 1.342l-.887.443A1.69 1.69 0 0 1 0 13.101V2.25Z"></path>
                                  <path d="M5 10.7a.7.7 0 0 1 .7-.7h4.6a.7.7 0 1 1 0 1.4H7.36l.136.237c.098.17.193.336.284.491.283.483.554.907.855 1.263.572.675 1.249 1.109 2.365 1.109 1.18 0 2.038-.423 2.604-1.039.576-.626.896-1.5.896-2.461 0-.99-.42-1.567-.807-1.998a.75.75 0 1 1 1.115-1.004C15.319 8.568 16 9.49 16 11c0 1.288-.43 2.54-1.292 3.476C13.838 15.423 12.57 16 11 16c-1.634 0-2.706-.691-3.51-1.64-.386-.457-.71-.971-1.004-1.472L6.4 12.74v2.56a.7.7 0 1 1-1.4 0v-4.6ZM2.95 4.25a.75.75 0 0 1 .75-.75h2a.75.75 0 0 1 0 1.5h-2a.75.75 0 0 1-.75-.75ZM3.7 6.5a.75.75 0 0 0 0 1.5h4.6a.75.75 0 0 0 0-1.5H3.7Z"></path>
                              </svg>
                            </a>
                          ) : (
                            <span className="payment-history-link-disabled">
                              {t("page.paymentHistory.table.noInvoice")}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                size="sm"
                className="payment-history-pagination"
                infoBelow={
                  <span className="payment-history-page-info">
                    {t("page.paymentHistory.pagination.pageInfo", {
                      page,
                      totalPages,
                    })}
                  </span>
                }
              />
            </div>
          ) : (
            <p className="payment-history-empty">
              {t("page.paymentHistory.noData")}
            </p>
          )}
        </section>
      )}
    </div>
  );
}
